import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAdminModules } from '../../hooks/useAdminModules';
import LessonManager from './LessonManager';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiChevronDown, FiChevronRight, FiGripVertical } = FiIcons;

const ModuleManager = ({ courseId, modules }) => {
  const { createModule, updateModule, deleteModule, reorderModules } = useAdminModules();
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [editingModule, setEditingModule] = useState(null);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [showNewModuleForm, setShowNewModuleForm] = useState(false);

  const handleCreateModule = async () => {
    if (!newModuleTitle.trim()) return;

    const orderIndex = modules.length;
    await createModule(courseId, {
      title: newModuleTitle,
      order_index: orderIndex
    });
    
    setNewModuleTitle('');
    setShowNewModuleForm(false);
  };

  const handleUpdateModule = async (moduleId, title) => {
    await updateModule(moduleId, { title });
    setEditingModule(null);
  };

  const handleDeleteModule = async (moduleId) => {
    if (window.confirm('Are you sure you want to delete this module? This will also delete all lessons in this module.')) {
      await deleteModule(moduleId);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    reorderModules(modules, sourceIndex, destinationIndex);
  };

  const toggleModuleExpansion = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Course Modules</h2>
        <button
          onClick={() => setShowNewModuleForm(true)}
          className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Module</span>
        </button>
      </div>

      {/* New Module Form */}
      <AnimatePresence>
        {showNewModuleForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={newModuleTitle}
                onChange={(e) => setNewModuleTitle(e.target.value)}
                placeholder="Module title..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateModule()}
              />
              <button
                onClick={handleCreateModule}
                className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowNewModuleForm(false);
                  setNewModuleTitle('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modules List */}
      {modules.length === 0 ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiPlus} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No modules yet</h3>
          <p className="text-gray-500 mb-4">Create your first module to organize course content</p>
          <button
            onClick={() => setShowNewModuleForm(true)}
            className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            Add First Module
          </button>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="modules">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                {modules.map((module, index) => (
                  <Draggable key={module.id} draggableId={module.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`border border-gray-200 rounded-lg overflow-hidden ${
                          snapshot.isDragging ? 'shadow-lg' : ''
                        }`}
                      >
                        {/* Module Header */}
                        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div {...provided.dragHandleProps} className="cursor-grab">
                              <SafeIcon icon={FiGripVertical} className="w-5 h-5 text-gray-400" />
                            </div>
                            
                            <button
                              onClick={() => toggleModuleExpansion(module.id)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              <SafeIcon 
                                icon={expandedModules.has(module.id) ? FiChevronDown : FiChevronRight} 
                                className="w-4 h-4 text-gray-600" 
                              />
                            </button>

                            {editingModule === module.id ? (
                              <input
                                type="text"
                                defaultValue={module.title}
                                onBlur={(e) => handleUpdateModule(module.id, e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleUpdateModule(module.id, e.target.value);
                                  }
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-nutrition-500"
                                autoFocus
                              />
                            ) : (
                              <h3 className="font-semibold text-gray-900">
                                Module {index + 1}: {module.title}
                              </h3>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">
                              {module.lessons?.length || 0} lessons
                            </span>
                            
                            <button
                              onClick={() => setEditingModule(module.id)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            
                            <button
                              onClick={() => handleDeleteModule(module.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Module Content - Lessons */}
                        <AnimatePresence>
                          {expandedModules.has(module.id) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="border-t border-gray-200"
                            >
                              <LessonManager
                                moduleId={module.id}
                                lessons={module.lessons || []}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </motion.div>
  );
};

export default ModuleManager;